import { useContext, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { Box, Button, ButtonGroup, Heading, Paragraph, Text, TextButton } from '@zillow/constellation-10';

import { UpsellCard } from './UpsellCard';

import { useSelector } from '../../../../../selectors/useSelector';
import { AppSelectors } from '../../../../../selectors';

import { PaymentGatewayContext } from '../../../../PaymentGateway/context';

import { default as pgActions } from '../../../../PaymentGateway/actions';
import PropertiesActions from '../../../../../actions/PropertiesActions';

import PropertyConstants from '../../../../../constants/PropertyConstants';
import { DISPLAY_CONTEXT } from '../../../../PaymentGateway/constants';
import PayAndPublishCSEvents from '../../../../../model/ClickstreamEvent/PayAndPublishCSEvents';

import { getFreemiumProductByType } from '../../../../../utils/upsellUtils';

import {
  cardContainerStyles,
  graphContainerStyles,
  mobileButtonGroupStyles,
  rightContainerStyles,
  topContainerStyles,
} from './styles';

export type ProductTypeId =
  | typeof PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM
  | typeof PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.FREE;

const BAR_CHART_DATA = [
  {
    name: 'Premium',
    pv: 17,
  },
  {
    name: 'Free',
    pv: 13,
  },
];

const BAR_SIZE = 50; // px
const OUTER_PADDING_PERCENT = 0.15; // 15% on each side

export const ValueOfPremium = () => {
  const dispatch = useDispatch();
  const { reducerDispatch: pgReducerDispatch } = useContext(PaymentGatewayContext);

  const { currentProperty, freemiumProducts, user } = useSelector(
    (state) => ({
      currentProperty: state.properties.currentProperty,
      freemiumProducts: state.properties.currentProperty.freemium.products,
      user: state.user,
    }),
    shallowEqual,
  );

  const [containerWidth, setContainerWidth] = useState(0);

  const outerPaddingPx = containerWidth < 300 ? 0 : Math.round(containerWidth * OUTER_PADDING_PERCENT);

  const updateSelectedProductType = (selectedProductType: ProductTypeId) => {
    dispatch(
      PropertiesActions.updateCurrentProperty({
        selectedProductType,
      }),
    );
  };

  const updateDisplayPaymentGateway = (value: boolean) => {
    pgActions.updateAllData({
      reducerDispatch: pgReducerDispatch,
      data: {
        displayPaymentGateway: value,
        displayContext: DISPLAY_CONTEXT.MODULAR_LISTING,
      },
    });
  };

  const isScreenLgGTE = useSelector((state) => AppSelectors.makeIsScreenLgGTE(state));

  const premiumProduct = getFreemiumProductByType(freemiumProducts, PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM);
  const premiumPrice = premiumProduct?.price;

  const handlePublishClick = (type: ProductTypeId, from: 'top' | 'bottom') => {
    updateSelectedProductType(type);
    updateDisplayPaymentGateway(true);

    if (from === 'top') {
      if (type === PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM) {
        PayAndPublishCSEvents.StartActivatePremiumTop({ currentProperty, user });
      } else {
        PayAndPublishCSEvents.StartActivateFreemiumTop({ currentProperty, user });
      }
    }

    if (from === 'bottom') {
      if (type === PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM) {
        PayAndPublishCSEvents.StartActivatePremiumBottom({ currentProperty, user });
      } else {
        PayAndPublishCSEvents.StartActivateFreemiumBottom({ currentProperty, user });
      }
    }
  };

  return (
    <Box>
      <Box data-testid="ValueOfPremium-Top" css={topContainerStyles}>
        <Box css={{ width: { base: '100%', lg: '50%' } }}>
          <Heading level={1} textStyle="heading-md">
            {isScreenLgGTE
              ? 'It’s time to publish! Reach more renters with premium'
              : 'Reach more renters with premium'}
          </Heading>
          <Paragraph
            css={{
              fontSize: 'heading-xs',
              marginTop: '600',
              marginBottom: { base: '600', lg: '800' },
            }}
          >
            Get up to <strong>30% more leads</strong> on average vs free
            <Text textStyle="fineprint-sm" css={{ verticalAlign: 'top' }}>
              &nbsp;^
            </Text>
          </Paragraph>
          {isScreenLgGTE && (
            <>
              {premiumPrice && (
                <Paragraph>
                  <strong>{premiumPrice}</strong> one-time payment
                </Paragraph>
              )}
              <ButtonGroup aria-label="Publish with premium or free" css={{ margin: '{spacing.600} 0' }}>
                <Button
                  emphasis="filled"
                  onClick={() => handlePublishClick(PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM, 'top')}
                  data-eventid={PayAndPublishCSEvents.StartActivatePremiumTop.data_event_id}
                >
                  Publish with premium
                </Button>
                <TextButton
                  onClick={() => handlePublishClick(PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.FREE, 'top')}
                  css={{ textAlign: 'left' }}
                  data-eventid={PayAndPublishCSEvents.StartActivateFreemiumTop.data_event_id}
                >
                  Publish for free
                </TextButton>
              </ButtonGroup>
              <Paragraph textStyle="fineprint-sm">
                ^ National average of internal Zillow Rentals leads data Jan-Apr 2025 for listings with applications
                enabled.
              </Paragraph>
            </>
          )}
        </Box>
        <Box css={rightContainerStyles}>
          <Box css={graphContainerStyles}>
            <Heading
              level={2}
              textStyle="heading-xs"
              css={{
                padding: '400',
                textAlign: 'center',
              }}
            >
              Leads in first 14 days
              <Text textStyle="fineprint-sm" css={{ verticalAlign: 'top' }}>
                &nbsp;^
              </Text>
            </Heading>
            <ResponsiveContainer
              width={'100%'}
              height={175}
              onResize={(width: number, height: number) => setContainerWidth(width)}
            >
              <BarChart data={BAR_CHART_DATA} margin={{ left: -16, right: 24, top: 8 }} barSize={BAR_SIZE}>
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: 'black' }}
                  padding={{ left: outerPaddingPx, right: outerPaddingPx }}
                />
                <YAxis ticks={[0, 5, 10, 15, 20]} axisLine={false} tickLine={false} />
                <CartesianGrid vertical={false} />
                <Bar dataKey="pv">
                  {BAR_CHART_DATA.map((entry) => (
                    <Cell fill={entry.name === 'Premium' ? '#F2A619' : '#D1D1D5'} key={`cell-${entry.name}`} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
          {!isScreenLgGTE && (
            <>
              {premiumPrice && (
                <Paragraph css={{ textAlign: 'center', marginTop: '600' }}>
                  <strong>{premiumPrice}</strong> one-time payment
                </Paragraph>
              )}
              <ButtonGroup aria-label="Publish with premium or free" css={mobileButtonGroupStyles}>
                <Button
                  emphasis="filled"
                  onClick={() => handlePublishClick(PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM, 'top')}
                  data-eventid={PayAndPublishCSEvents.StartActivatePremiumTop.data_event_id}
                >
                  Publish with premium
                </Button>
                <TextButton
                  onClick={() => handlePublishClick(PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.FREE, 'top')}
                  data-eventid={PayAndPublishCSEvents.StartActivateFreemiumTop.data_event_id}
                >
                  Publish for free
                </TextButton>
              </ButtonGroup>
              <Paragraph textStyle="fineprint-sm" css={{ alignSelf: 'start' }}>
                ^ National average of internal Zillow Rentals leads data Jan-Apr 2025 for listings with applications
                enabled.
              </Paragraph>
            </>
          )}
        </Box>
      </Box>
      <Box data-testid="ValueOfPremium-Bottom">
        <Heading
          level={3}
          textStyle="heading-sm"
          css={{ textAlign: { base: 'left', lg: 'center' }, paddingTop: '1000' }}
        >
          Boost your listing with increased exposure and insights
        </Heading>
        <Box css={cardContainerStyles}>
          <UpsellCard
            type={PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM}
            onPublishClick={() => handlePublishClick(PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM, 'bottom')}
          />
          <UpsellCard
            type={PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.FREE}
            onPublishClick={() => handlePublishClick(PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.FREE, 'bottom')}
          />
        </Box>
      </Box>
    </Box>
  );
};
